import React, { useRef, useEffect, useState } from 'react';
import classnames from 'classnames';
import { isIE } from 'react-device-detect';
import reactMergeRefs from 'react-merge-refs';
import ResizeObserver from 'resize-observer-polyfill';
import { isUndefined, isFunction } from 'lodash';

// local dependencies
import { isMobile } from 'core/modules/device-detect';
import { Icon } from 'modules/icon-bank';

/**
 * @desc A react presentation component used to render overlays across the application.
 * @example
 * import { overlay as OverlayView } from 'views/generic/overlay';
 */
export const overlay = React.forwardRef( ( props, forwardedRef ) => {

	const overlayContentPanRef = useRef( null );

    const [ shouldFixedHeight, setShouldFixedHeight ] = useState( false );
    
    let _resizeObserver;

	useEffect( () => {

        /**
         * If the browser is internet explorer
         * and the cotainer is more than 80px from top.
         * This is done because IE does not support scroll with max-height.
         */
        if( isIE && ( 80 > overlayContentPanRef.current.offsetTop ) ) {
            setShouldFixedHeight( true );
        }

        /**
         * @author Aman Kalra
         * @date 07/02/2020
         * @reviewer Sourabh Kumar
         * This has been done for Defect #13579
         * The issue was that the element which triggered the overlay was not losing focus
         * So when the user would press enter again it will trigger the action on the active element again
         * To solve this problem tabIndex="-1" has been added to div.ui-overlay__content-pane
         * And an id = "overlay-pane" has been added to the same element.
         * @TODO: This works for now but in future a more generic approach should be used.
         */
        document.getElementById( 'overlay-pane' ).focus();

        /**
         * If the browser is IE,
         * then observe the height of the container.
         * If it changes, the following code takes care of the overflow support part.
         */
        if ( isIE ) {
            _resizeObserver = new ResizeObserver( () => {
                if ( overlayContentPanRef.current ) {
                    if ( window.innerHeight - 150 <= overlayContentPanRef.current.offsetHeight ) {
                        setShouldFixedHeight( true );
                        
                    }
                }
            } );
            _resizeObserver.observe( overlayContentPanRef.current );
        }
        
        return () => {
            
            /**
             * When the component is unmounting,
             * the observer on the height of the wrapping container
             * should be deleted.
             */
            if ( ! isUndefined( _resizeObserver ) ) {
                if ( isFunction( _resizeObserver.unobserve ) ) {
                    try {
                        _resizeObserver.unobserve( overlayContentPanRef.current );
                    } catch {
                        return null;
                    }

                    try {
                        _resizeObserver.disconnect( overlayContentPanRef.current );
                    } catch {
                        return null;
                    }
                }
            }
        };
	}, [] );

    // close button style props
    const closeButtonProps = isMobile() ? { fill: '#000', width: 16, height: 16 } : { fill: '#ffffff', width: 20, height: 20 };

    // body content class name
    const contentBodyClassName = classnames( 'ui-overlay__content-pane__content', {
        'ui-overlay__content-pane__content--with-padding': props.enablePadding,
        'ui-overlay__content-pane__content--fixed-to-bottom': props.fixedToBottom,
        'ui-overlay__content-pane__content--with-overflow': ! props.ignoreOverflow,
    } );

    const contentPaneClassName = classnames( 'ui-overlay__content-pane', {
		'ui-overlay__content-pane--transparent-background': props.transparentBackground,
		'ui-overlay__content-pane__fixed-height': shouldFixedHeight
	} );


    return (
        <div className='ui-overlay' data-ignore-outclick={ props.ignoreOutsideClick ? true : undefined }>

            {/* backdrop */}
            <div className='ui-overlay__bg'></div>

            {/* content pane view */}
            <div className={ contentPaneClassName } id={'overlay-pane'} tabIndex="-1" ref = { reactMergeRefs( [ forwardedRef, overlayContentPanRef ] )  } >
                {/* content view */}
                <div className={ contentBodyClassName } onClick = { () => { props.fixedToBottom ? props.closeOverlay( { closeAll: false } ) : null; } } >

                    { ! props.fixedToBottom ? (
                        <props.component
                            { ...props.data }
							config = { props.config }
                            closeOverlay = { props.closeOverlay }
                        />
                    ) : (

                        // view to stick to the bottom of the mobile screen
                        <div className= { 'ui-overlay__content-pane__content__view' } onClick={ ( event ) => event.stopPropagation() } >
                            <props.component
                                { ...props.data }
								config = { props.config }
                                closeOverlay = { props.closeOverlay }
                            />
                        </div>
                    ) }
                </div>

                {/* close button */}
                {
                    props.showCloseButton ? (
                        <div className='ui-overlay__content-pane__close' onClick={ props.closeOverlay } data-action-name='closeOverlay'>
                            <Icon id='close' { ...closeButtonProps }/>
                        </div>
                    ) : null
                }
            </div>
        </div>
    );
} );

// display name
overlay.displayName = 'overlay';
